<script lang="ts" setup>
import { usePosSetupStore } from "@/store/posSetupStore";
import { useUserModeChange } from "@/store/useUserModeChange";
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserRole } from "@/utils/useUserRole";
import { requiredValidator } from "@validators";
import { toast } from "vue3-toastify";

// Interface
interface Emit {
  (e: "closeDialog", value: boolean): void;
}

interface Props {
  isVisible?: boolean;
  docketId: string;
}

// Props
const props = defineProps<Props>();

// Emit
const emit = defineEmits<Emit>();

// Store
const userRole = useUserRole();
const { permissionListMap } = storeToRefs(userRole);
const modeStore = useUserModeChange();
const { isUserMode } = storeToRefs(modeStore);
const posSetupStore = usePosSetupStore();

// Data
const email = ref<string>(pb.authStore.model?.email);
const userId = ref<string>(pb?.authStore?.model?.id);
const refReceiptVForm = ref<any>();
const docketId = ref<string>("");
const isHistoryDialogOpen = ref<boolean>(false);
const hasPrinter = ref<boolean>(false);
const printer = ref<string>('');
const printerList = ref<Array<{ id: string; name: string }>>([]);

// Check if user has View or higher permission for dockets
const canViewDockets = computed(() => {
  return permissionListMap.value['dockets']?.includes('View') ?? false;
});

// Check if POS setup has a printer
const checkPrinter = async () => {
  try {
    const posSetup = await posSetupStore.load();
    if (posSetup) {
      hasPrinter.value = !!posSetup.receiptPrinter;
      printer.value = posSetup.receiptPrinter;

      const printers = await pb.collection("printers").getFullList({filter:'printerType!="Label"', requestKey: null});
      printerList.value = printers.map((item) => ({
        id: item.id,
        name: item.name
      }));
    } else {
      hasPrinter.value = false;
    }
  } catch (e) {
    console.error("Error checking printer:", e);
    hasPrinter.value = false;
  }
};

// Method
const emailReceipt = async () => {
  const { valid } = await refReceiptVForm.value?.validate();
  if (!valid) return;
  if (docketId.value) {
    const data = {
      docketID: docketId.value,
      email: email.value,
      requiresEmail: true,
      createdBy: userId.value,
    };

    const response = await pb.collection("docketReceipts").create(data);

    if (response) {
      toast.success("Email Receipt Sent", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      closeDialog();
    }
  }
};

// Update requiresPrinting in docketPayments
const printReceipt = async () => {
  if (docketId.value) {
    const data = {
      docketID: docketId.value,
      email: email.value,
      requiresPrinting: true,
      printer: printer.value,
    };

    const response = await pb.collection("docketReceipts").create(data);

    if (response) {
      toast.success("Receipt Printed", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      closeDialog();
    }
  }
};

const closeDialog = () => {
  emit("closeDialog", false);
};

// watch
watchEffect(() => {
  if (props?.docketId) {
    docketId.value = props?.docketId;
  }
});

// Check for printer when dialog opens
watch(() => props.isVisible, (newVal) => {
  if (newVal) {
    checkPrinter();
  }
});

// Check on mount as well
onMounted(() => {
  if (props.isVisible) {
    checkPrinter();
  }
});
</script>

<template>
  <VDialog
    persistent
    :model-value="props.isVisible"
    no-click-animation
    width="600"
    @update:is-drawer-open="closeDialog"
  >
    <VCard flat>
      <div
        class="d-flex align-center justify-space-between bg-dialogHeaderColor"
      >
        <!-- 👉 Header -->
        <VCardTitle> Create Receipt </VCardTitle>
        <VBtn
          icon="mdi-close"
          size="large"
          variant="text"
          class="rounded-0"
          color=""
          @click="closeDialog"
        ></VBtn>
      </div>
      <VCardText>
        <VResponsive class="mx-auto mt-4" max-width="344">
          <div class="mt-2">
            <VForm ref="refReceiptVForm">
              <v-row align="center">
                <v-col cols="10">
                  <VTextField
                    v-model="email"
                    :rules="[requiredValidator]"
                    label="Email Address"
                  />
                </v-col>
                <v-col cols="2">
                  <IconBtn
                    :disabled="
                      permissionListMap['dockets'] &&
                      !permissionListMap['dockets']?.includes('View')
                    "
                    color="error"
                    @click="isHistoryDialogOpen = true"
                  >
                    <VIcon icon="mdi-history" />
                    <VTooltip activator="parent" open-delay="400">
                      History
                    </VTooltip>
                  </IconBtn>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-if="printerList.length > 1 && canViewDockets && hasPrinter"
                    v-model="printer"
                    :items="printerList"
                    item-title="name"
                    item-value="id"
                    label="Printer"
                  />
                </v-col>
              </v-row>
            </VForm>
          </div>
        </VResponsive>
        <div class="d-flex justify-center flex-wrap gap-4 my-4 mt-6">
          <VBtn color="primary" @click="emailReceipt"> Email Receipt </VBtn>
          <VBtn v-if="hasPrinter && canViewDockets" color="primary" @click="printReceipt">
            Print Receipt
          </VBtn>
          <VBtn color="error" @click="closeDialog"> Close </VBtn>
        </div>
      </VCardText>
    </VCard>
  </VDialog>

  <!-- History dialog -->
  <HistoryDialog
    :is-visible="isHistoryDialogOpen"
    :docket-id="docketId"
    @close-dialog="
      () => {
        isHistoryDialogOpen = false;
      }
    "
  />
</template>
